import { useContext } from 'react';
import { ButtonGroup } from '@mantine/core';
import { MenuMappingContext } from 'components/MenuManagement/MenuEdit/MenuMapping/MenuMapping';
import {
  PARENT_NAMES,
  MAPPING_STATUS,
  GRANDPARENT_NAMES,
  MAPPING_FILTERS,
  MAPPING_BADGE_LABEL,
} from 'constants/mapping';
import { CAMEL_TYPES } from 'constants/menuManagement';
import { uniqueParentsFilter } from 'helpers/mappingColumns';

import FilterGroup from './FilterGroup';

interface MenuMappingFiltersProps {
  isModifierSelected?: boolean;
  setFilteredCategories: (categories: string[]) => void;
  setFilteredParents?: (parents: string[]) => void;
  setFilteredStatuses: (statuses: string[]) => void;
}

export default function MenuMappingFilters({
  isModifierSelected,
  setFilteredCategories,
  setFilteredParents,
  setFilteredStatuses,
}: MenuMappingFiltersProps) {
  const itemType = isModifierSelected ? CAMEL_TYPES.modifiers : CAMEL_TYPES.items;

  const { appMenu } = useContext(MenuMappingContext);

  let categoryFilters: { text: string; value: string }[] = [];
  let parentFilters: { text: string; value: string }[] = [];

  if (appMenu && appMenu[itemType] && Object.keys(appMenu[itemType]).length > 0) {
    categoryFilters = uniqueParentsFilter({
      fieldName: PARENT_NAMES,
      appMenu,
      itemType,
    });

    parentFilters = uniqueParentsFilter({
      fieldName: GRANDPARENT_NAMES,
      appMenu,
      itemType,
    });
  }

  const statusFilters = [
    {
      text: MAPPING_BADGE_LABEL.MAPPED,
      value: [MAPPING_STATUS.MAPPED],
    },
    {
      text: MAPPING_BADGE_LABEL.EDITED,
      value: [MAPPING_STATUS.STAGED_UNMAPPED, MAPPING_STATUS.STAGED_MAPPED],
    },
    {
      text: MAPPING_BADGE_LABEL.UNMAPPED,
      value: [MAPPING_STATUS.UNMAPPED, MAPPING_STATUS.POS_MISSING, MAPPING_STATUS.STAGED_UNMAPPED],
    },
  ];

  const handleFilterChange = (label: string, values: string[] | []) => {
    switch (label) {
      case MAPPING_FILTERS.STATUS:
        setFilteredStatuses(values);
        break;
      case MAPPING_FILTERS.PARENT:
        if (setFilteredParents) setFilteredParents(values);
        break;
      // categories and mod groups use the same filter
      default:
        setFilteredCategories(values);
        break;
    }
  };

  const filters = isModifierSelected
    ? [
        { label: MAPPING_FILTERS.STATUS, options: statusFilters },
        { label: MAPPING_FILTERS.MOD_GROUP, options: categoryFilters },
        { label: MAPPING_FILTERS.PARENT, options: parentFilters },
      ]
    : [
        { label: MAPPING_FILTERS.STATUS, options: statusFilters },
        { label: MAPPING_FILTERS.CATEGORY, options: categoryFilters },
      ];

  return (
    <ButtonGroup>
      {filters.map(filter => (
        <FilterGroup
          options={filter.options}
          label={filter.label}
          handleFilterChange={handleFilterChange}
          key={`${filter.label}_filter_group`}
        />
      ))}
    </ButtonGroup>
  );
}
